/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2019 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import EditorComponentBase from './EditorComponentBase';
import PrefabDefinition from './PrefabDefinition';
import PrefabInstance from './PrefabInstance';
import PrefabLoader from './PrefabLoader';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

@cc._decorator.ccclass
@cc._decorator.executeInEditMode
export default class PrefabManager extends EditorComponentBase
{
    @property()
    protected set DestroyPrefabs(value: boolean)
    {
        if (!value) { return; }
        this.destroyPrefabs();
    }
    protected get DestroyPrefabs(): boolean { return false; }

    @property()
    protected set DestroyAll(value: boolean)
    {
        if (!value) { return; }
        this.destroyPrefabs();
        this.node.destroy();
    }
    protected get DestroyAll(): boolean { return false; }

    @property({ type: [PrefabInstance], readonly: true })
    protected prefabs: PrefabInstance[] = [];

    private _prefabLoader: PrefabLoader = null;

    protected onLoad(): void
    {
        super.onLoad();
        this.resetInEditor();
    }

    protected resetInEditor(): void
    {
        this.configurePrefabLoader();
        this.updatePrefabInstances();
    }

    private configurePrefabLoader(): void
    {
        try
        {
            this._prefabLoader = cc.director.getScene().getComponentsInChildren(PrefabLoader)[0];
        }
        catch (error)
        {
            this.error(error.message ? error.message : error);
        }
    }

    private updatePrefabInstances(): void
    {
        if (!this._prefabLoader)
        {
            this.error('No prefab loader, please releoad scene.');
            return;
        }

        const newPrefabs: PrefabInstance[] = [];

        this._prefabLoader.PrefabDefinitions.forEach(prefabDef =>
            {
                const fnCreate: () => void = () =>
                {
                    this.loadPrefabDefinition(prefabDef);
                };

                const existingInstance: PrefabInstance = this.prefabs
                    .find(prefabInstance => prefabInstance.Path === prefabDef.PrefabPath);

                if (existingInstance)
                {
                    existingInstance.FnCreate = fnCreate;
                    newPrefabs.push(existingInstance);
                }
                else
                {
                    newPrefabs.push(new PrefabInstance(prefabDef.PrefabPath, fnCreate));
                }
            });

        const orphans: PrefabInstance[] = this.prefabs.filter(prefabInstance =>
            {
                return prefabInstance.SceneNode
                    && !newPrefabs.find(newPrefab => newPrefab.Path === prefabInstance.Path);
            });

        this.prefabs = newPrefabs;
        if (orphans && orphans.length > 0)
        {
            this.prefabs.push(...orphans);
        }
    }

    private loadPrefabDefinition(prefabDef: PrefabDefinition, callBack: Function = null): void
    {
        const url: string = cc.url.raw(`resources/${prefabDef.PrefabPath}.prefab`);
        cc.loader.load(url, (error: Error, prefab: cc.Prefab) =>
            {
                const safeCallBack: Function = () => callBack && callBack();

                if (error)
                {
                    this.error(error.message);
                    safeCallBack();
                    return;
                }

                this.loadPrefab(prefab, prefabDef, url);
                safeCallBack();
            });
    }

    private loadPrefab(prefab: cc.Prefab, prefabDef: PrefabDefinition, url: string): void
    {
        const parent: cc.Node = prefabDef.ParentNode ? prefabDef.ParentNode : this.node;
        const instance: cc.Node = cc.instantiate(prefab);

        parent.insertChild(instance, prefabDef.ZOrder);

        cc.loader.release(url);

        const localInstance: PrefabInstance
            = this.prefabs.find(prefabInstance => prefabInstance.Path === prefabDef.PrefabPath);
        if (localInstance)
        {
            localInstance.SceneNode = instance;
        }
    }

    private destroyPrefabs(): void
    {
        this.prefabs.forEach(prefab => prefab.Destroy = true);
    }
}
